/**
 * Schedule form component with Vuexy styling.
 * Provides form for creating and editing schedules with organized sections.
 */

// React Imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// MUI Imports
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

// Component Imports
import { TextField } from '@core/components/mui';
import { scheduleService } from '../../services/scheduleService';
import { agentService } from '../../services/agentService';
import { useNotificationStore } from '../../store/notificationStore';
import { validateCronExpression } from '../../utils/cronValidation';
import { CronBuilder } from './CronBuilder';

// Types
import type {
  CreateScheduleRequest,
  UpdateScheduleRequest,
} from '../../types/schedule';
import type { ScheduleType } from '../../types/schedule';
import { ScheduleTypes } from '../../types/schedule';
import type { AgentDefinitionDto } from '../../types/agent';

/**
 * Schedule form component for creating and editing schedules.
 */
export const ScheduleForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { showNotification } = useNotificationStore();

  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [agentDefinitionId, setAgentDefinitionId] = useState('');
  const [scheduleType, setScheduleType] = useState<ScheduleType>(ScheduleTypes.Cron);
  const [cronExpression, setCronExpression] = useState('');
  const [cronError, setCronError] = useState<string | null>(null);
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [interval, setInterval] = useState('');
  const [timeZone, setTimeZone] = useState('UTC');
  const [isActive, setIsActive] = useState(true);
  const [parametersJson, setParametersJson] = useState('');
  const [maxRetries, setMaxRetries] = useState<number | ''>('');
  const [retryDelay, setRetryDelay] = useState('');

  useEffect(() => {
    loadAgents();
    if (isEdit && id) {
      loadSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadAgents = async () => {
    try {
      const result = await agentService.getAgents({ pageSize: 1000, isActive: true });
      setAgents(result.items);
    } catch (err: any) {
      showNotification(err.message || 'Failed to load agents', 'error');
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadSchedule = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await scheduleService.getSchedule(id);
      setName(data.name);
      setDescription(data.description || '');
      setAgentDefinitionId(data.agentDefinitionId);
      setScheduleType(data.scheduleType);
      setCronExpression(data.cronExpression || '');
      setScheduledDateTime(data.scheduledDateTime || '');
      setInterval(data.interval || '');
      setTimeZone(data.timeZone || 'UTC');
      setIsActive(data.isActive);
      setParametersJson(data.parametersJson || '');
      setMaxRetries(data.maxRetries || '');
      setRetryDelay(data.retryDelay || '');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load schedule';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!agentDefinitionId) {
      setError('Agent is required');
      return;
    }

    if (scheduleType === ScheduleTypes.Cron) {
      if (!cronExpression.trim()) {
        setError('Cron expression is required for Cron schedule type');
        setCronError('Cron expression is required');
        return;
      }
      if (!validateCronExpression(cronExpression.trim())) {
        setError('Invalid cron expression. Please check the format.');
        setCronError('Invalid cron expression format');
        return;
      }
      setCronError(null);
    }

    if (scheduleType === ScheduleTypes.OneTime && !scheduledDateTime) {
      setError('Scheduled date/time is required for One-Time schedule type');
      return;
    }

    if (scheduleType === ScheduleTypes.Interval && !interval.trim()) {
      setError('Interval is required for Interval schedule type');
      return;
    }

    setSaving(true);
    try {
      const request: CreateScheduleRequest | UpdateScheduleRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        agentDefinitionId,
        scheduleType,
        cronExpression: scheduleType === ScheduleTypes.Cron ? cronExpression.trim() : undefined,
        scheduledDateTime: scheduleType === ScheduleTypes.OneTime ? scheduledDateTime : undefined,
        interval: scheduleType === ScheduleTypes.Interval ? interval.trim() : undefined,
        timeZone,
        isActive,
        parametersJson: parametersJson.trim() || undefined,
        maxRetries: maxRetries !== '' ? Number(maxRetries) : undefined,
        retryDelay: retryDelay.trim() || undefined,
      };

      if (isEdit && id) {
        await scheduleService.updateSchedule(id, request);
        showNotification('Schedule updated successfully', 'success');
      } else {
        await scheduleService.createSchedule(request);
        showNotification('Schedule created successfully', 'success');
      }
      navigate('/schedules');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save schedule';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading || loadingAgents) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent sx={{ p: 6 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <IconButton onClick={() => navigate('/schedules')} sx={{ mr: -1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 600, flex: 1 }}>
              {isEdit ? 'Edit Schedule' : 'Create Schedule'}
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Name"
                fullWidth
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Agent</InputLabel>
                <Select
                  value={agentDefinitionId}
                  label="Agent"
                  onChange={(e) => setAgentDefinitionId(e.target.value)}
                >
                  {agents.map((agent) => (
                    <MenuItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Schedule Type</InputLabel>
                <Select
                  value={scheduleType}
                  label="Schedule Type"
                  onChange={(e) => setScheduleType(e.target.value as ScheduleType)}
                >
                  <MenuItem value={ScheduleTypes.Cron}>Cron</MenuItem>
                  <MenuItem value={ScheduleTypes.Interval}>Interval</MenuItem>
                  <MenuItem value={ScheduleTypes.OneTime}>One-Time</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {scheduleType === ScheduleTypes.Cron && (
              <Grid size={{ xs: 12 }}>
                <CronBuilder
                  value={cronExpression}
                  onChange={(value) => {
                    setCronExpression(value);
                    setCronError(null);
                    if (value && !validateCronExpression(value)) {
                      setCronError('Invalid cron expression format');
                    }
                  }}
                  error={!!cronError}
                  helperText={cronError || 'Standard cron format (minute hour day month day-of-week)'}
                />
              </Grid>
            )}

            {scheduleType === ScheduleTypes.OneTime && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Scheduled Date/Time"
                  fullWidth
                  required
                  type="datetime-local"
                  value={scheduledDateTime}
                  onChange={(e) => setScheduledDateTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}

            {scheduleType === ScheduleTypes.Interval && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Interval"
                  fullWidth
                  required
                  placeholder="PT1H (1 hour), PT30M (30 minutes), etc."
                  helperText="ISO 8601 duration format (e.g., PT1H for 1 hour, PT30M for 30 minutes)"
                  value={interval}
                  onChange={(e) => setInterval(e.target.value)}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Time Zone"
                fullWidth
                value={timeZone}
                onChange={(e) => setTimeZone(e.target.value)}
                helperText="e.g., UTC, America/New_York, Europe/London"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                label="Active"
              />
            </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Advanced Settings
                </Typography>
              </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Parameters JSON"
                fullWidth
                multiline
                rows={4}
                placeholder='{"key": "value"}'
                helperText="Optional JSON parameters to pass to the agent"
                value={parametersJson}
                onChange={(e) => setParametersJson(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Max Retries"
                fullWidth
                type="number"
                value={maxRetries}
                onChange={(e) => setMaxRetries(e.target.value === '' ? '' : Number(e.target.value))}
                helperText="Maximum number of retries on failure"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Retry Delay"
                fullWidth
                placeholder="PT5M"
                helperText="ISO 8601 duration format (e.g., PT5M for 5 minutes)"
                value={retryDelay}
                onChange={(e) => setRetryDelay(e.target.value)}
              />
            </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 6 }}>
                  <Button variant="outlined" onClick={() => navigate('/schedules')}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
